import React from 'react';
import { useParams } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { useSessionResolver } from '../hooks/useSessionResolver';
import { mapSessionToAppProps } from '../services/sessionMapper';
import App from '../App';

/**
 * Opaque session entry point: /compliance/:sessionId
 *
 * The URL contains ONLY the session ID — no branch, sender, transaction,
 * process codes, or document IDs. This page resolves the session server-side
 * and maps the resolved context onto the existing App props.
 *
 * While resolving, shows a loading state. On error, shows a localized message
 * with a retry action (capped at 3 attempts by the resolver) and never
 * navigates away or renders malformed data.
 */
function ComplianceSessionPage({ t }) {
  const { sessionId } = useParams();

  const token = process.env.REACT_APP_TOKEN_PASS;
  const region = '';
  const baseUrl = (process.env.REACT_APP_URL_VIACHEK_MOBILE || '').replace(
    '{NAME_REGION}',
    region
  );

  const { status, session, ownerSecret, error, retry, retryCount } =
    useSessionResolver({
      baseUrl,
      token,
      sessionId
    });

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="compliance-session__loading" role="status" aria-live="polite">
        {t ? t('loading') : 'Loading...'}
      </div>
    );
  }

  if (status === 'error') {
    const errorKey = `session.error.${error?.type || 'network'}`;
    const retriesExhausted = retryCount >= 3;
    return (
      <div className="compliance-session__error" role="alert">
        <p>{t ? t(errorKey) : 'Unable to load this page. Please try again.'}</p>
        {!retriesExhausted && (error?.type === 'network' || error?.type === 'timeout') && (
          <button type="button" onClick={retry}>
            {t ? t('retry') : 'Retry'}
          </button>
        )}
        {retriesExhausted && (
          <p>{t ? t('session.error.contactSupport') : 'Please contact support.'}</p>
        )}
      </div>
    );
  }

  // status === 'resolved' — map session context onto App props.
  const appProps = mapSessionToAppProps(session);
  return (
    <App
      {...appProps}
      complianceSessionId={sessionId}
      ownerSecret={ownerSecret || undefined}
    />
  );
}

export default withTranslation()(ComplianceSessionPage);
